import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import api, { trackVisit } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Calendar, 
  Clock, 
  User, 
  Phone, 
  Scissors, 
  CheckCircle, 
  CreditCard,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Tag,
  Copy,
  MessageCircle
} from 'lucide-react';

const PublicBooking = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Steps: 1: Form, 2: Payment, 3: Success
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [barbers, setBarbers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [depositSettings, setDepositSettings] = useState({ deposit_amount: 50, deposit_type: 'fixed' });
  const [settings, setSettings] = useState({ min_booking_hours: 2, max_booking_days: 7 });
  
  // Form state
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    barber_id: '',
    service_id: '',
    booking_date: '',
    booking_time: ''
  });
  
  // Selected service details
  const [selectedService, setSelectedService] = useState(null);
  
  // Deposit option: 'quarter' (25%), 'half' (50%), 'full' (100%), 'custom'
  const [depositOption, setDepositOption] = useState('quarter');
  const [customDeposit, setCustomDeposit] = useState('');
  
  // Minimum deposit amount
  const MIN_DEPOSIT = 10;
  
  // Booking result
  const [bookingResult, setBookingResult] = useState(null);
  
  // Check for payment success from URL
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  useEffect(() => {
    trackVisit('/public-booking');
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [barbersRes, categoriesRes, servicesRes, depositRes, settingsRes] = await Promise.all([
        api.get('/public/barbers'),
        api.get('/services/categories'),
        api.get('/services'),
        api.get('/payments/deposit-settings'),
        api.get('/public/settings')
      ]);
      setBarbers(barbersRes.data);
      setCategories(categoriesRes.data);
      setServices(servicesRes.data);
      setDepositSettings(depositRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setLoading(true);
    try {
      const response = await api.get(`/payments/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        // Payment successful - show success step
        setBookingResult({
          reference_number: response.data.reference_number || `KE-${Date.now()}`,
          payment_status: 'paid',
          amount: response.data.amount_total
        });
        setStep(3);
        toast.success(language === 'ar' ? 'تم الدفع والحجز بنجاح!' : 'Payment and booking successful!');
      } else {
        toast.error(language === 'ar' ? 'لم يتم إكمال الدفع' : 'Payment was not completed');
      }
    } catch (error) {
      toast.error(language === 'ar' ? 'خطأ في التحقق من الدفع' : 'Error checking payment status');
    } finally {
      setLoading(false);
    }
  };

  const handleServiceSelect = (serviceId) => {
    setFormData({ ...formData, service_id: serviceId });
    const service = services.find(s => s.id === serviceId);
    setSelectedService(service);
    // Reset deposit option when service changes
    setDepositOption('quarter');
  };

  const calculateDepositAmount = () => {
    // If no service selected, minimum deposit is 10 AED
    if (!selectedService) {
      return MIN_DEPOSIT;
    }
    
    const servicePrice = selectedService.price || 0;
    let calculatedAmount;
    
    switch (depositOption) {
      case 'quarter':
        calculatedAmount = Math.round(servicePrice * 0.25);
        break;
      case 'half':
        calculatedAmount = Math.round(servicePrice * 0.5);
        break;
      case 'full':
        calculatedAmount = servicePrice;
        break;
      case 'custom':
        calculatedAmount = parseInt(customDeposit) || MIN_DEPOSIT;
        break;
      default:
        calculatedAmount = Math.round(servicePrice * 0.25);
    }
    
    // Ensure minimum of 10 AED
    return Math.max(calculatedAmount, MIN_DEPOSIT);
  };

  const getDepositOptions = () => {
    if (!selectedService) return [];
    
    const price = selectedService.price || 0;
    const quarter = Math.max(Math.round(price * 0.25), MIN_DEPOSIT);
    const half = Math.max(Math.round(price * 0.5), MIN_DEPOSIT);
    const full = Math.max(price, MIN_DEPOSIT);
    
    return [
      { id: 'quarter', label: language === 'ar' ? 'ربع المبلغ' : 'Quarter', percentage: '25%', amount: quarter },
      { id: 'half', label: language === 'ar' ? 'نصف المبلغ' : 'Half', percentage: '50%', amount: half },
      { id: 'full', label: language === 'ar' ? 'المبلغ كامل' : 'Full', percentage: '100%', amount: full },
      { id: 'custom', label: language === 'ar' ? 'مبلغ مخصص' : 'Custom', percentage: '', amount: null }
    ];
  };

  const handleProceedToPayment = async (e) => {
    e.preventDefault();
    
    // Validate form - service is now optional
    if (!formData.full_name || !formData.phone || !formData.booking_date || !formData.booking_time) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const depositAmount = calculateDepositAmount();
      
      // Create appointment first (pending payment)
      const appointmentRes = await api.post('/public/book-with-deposit', {
        ...formData,
        deposit_amount: depositAmount,
        service_name: selectedService?.name_ar || selectedService?.name_en || 'بدون خدمة محددة'
      });
      
      const appointmentId = appointmentRes.data.appointment_id || appointmentRes.data.booking_id;
      
      // Create Stripe checkout session
      const checkoutRes = await api.post('/payments/checkout/create', {
        appointment_id: appointmentId,
        service_ids: formData.service_id ? [formData.service_id] : [],
        deposit_amount: depositAmount,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      if (checkoutRes.data.url) {
        window.location.href = checkoutRes.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || (language === 'ar' ? 'فشل إنشاء الحجز' : 'Failed to create booking'));
      setLoading(false);
    }
  };

  const copyReferenceNumber = () => {
    if (bookingResult?.reference_number) {
      navigator.clipboard.writeText(bookingResult.reference_number);
      toast.success(language === 'ar' ? 'تم نسخ رقم المرجع' : 'Reference number copied');
    }
  };

  const getMinDate = () => {
    const now = new Date();
    now.setHours(now.getHours() + settings.min_booking_hours);
    return now.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const now = new Date();
    now.setDate(now.getDate() + settings.max_booking_days);
    return now.toISOString().split('T')[0];
  };

  const getServicesByCategory = (categoryId) => {
    return services.filter(s => s.category_id === categoryId);
  };

  const Arrow = language === 'ar' ? ArrowLeft : ArrowRight;

  // Show loading if checking payment
  if (loading && searchParams.get('session_id')) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-white">
            {language === 'ar' ? 'جاري التحقق من الدفع...' : 'Verifying payment...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="bg-black/60 backdrop-blur-md border-b border-white/5 px-6 py-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src="/logo-gold.png" alt="KE" className="w-8 h-8 object-contain" />
            <h1 className="text-xl font-bold text-white">KE Gents Saloon</h1>
          </div>
          <a href="/" className="text-primary hover:underline text-sm">
            {language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
          </a>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-primary text-black' : 'bg-secondary'}`}>
                1
              </div>
              <span className="hidden sm:inline text-sm">
                {language === 'ar' ? 'البيانات' : 'Details'}
              </span>
            </div>
            <div className="w-8 h-0.5 bg-secondary" />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-primary text-black' : 'bg-secondary'}`}>
                2
              </div>
              <span className="hidden sm:inline text-sm">
                {language === 'ar' ? 'الدفع' : 'Pay'}
              </span>
            </div>
            <div className="w-8 h-0.5 bg-secondary" />
            <div className={`flex items-center gap-2 ${step >= 3 ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-primary text-black' : 'bg-secondary'}`}>
                3
              </div>
              <span className="hidden sm:inline text-sm">
                {language === 'ar' ? 'تأكيد' : 'Confirm'}
              </span>
            </div>
          </div>
        </div>

        {/* Step 1: Booking Form */}
        {step === 1 && (
          <div className="bg-card border border-white/5 rounded-xl p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Scissors className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-xl font-semibold mb-2">
                {language === 'ar' ? 'احجز موعدك' : 'Book Your Appointment'}
              </h2>
              <p className="text-muted-foreground text-sm">
                {language === 'ar' ? 'املأ البيانات وادفع العربون لتأكيد الحجز' : 'Fill in details and pay deposit to confirm'}
              </p>
            </div>

            <form onSubmit={handleProceedToPayment} className="space-y-6">
              {/* Personal Info */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <User className="w-4 h-4 inline me-1" />
                    {language === 'ar' ? 'الاسم الكامل' : 'Full Name'} *
                  </label>
                  <Input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder={language === 'ar' ? 'أدخل اسمك' : 'Enter your name'}
                    className="bg-secondary/50 border-white/10"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <Phone className="w-4 h-4 inline me-1" />
                    {language === 'ar' ? 'رقم الهاتف' : 'Phone Number'} *
                  </label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+971 XX XXX XXXX"
                    className="bg-secondary/50 border-white/10"
                    dir="ltr"
                    required
                  />
                </div>
              </div>

              {/* Service Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Tag className="w-4 h-4 inline me-1" />
                  {language === 'ar' ? 'اختر الخدمة (اختياري)' : 'Select Service (Optional)'}
                </label>
                <div className="space-y-3 max-h-64 overflow-y-auto p-2 bg-secondary/30 rounded-lg">
                  {categories.map(category => {
                    const categoryServices = getServicesByCategory(category.id);
                    if (categoryServices.length === 0) return null;
                    
                    return (
                      <div key={category.id}>
                        <p className="text-xs text-primary font-medium mb-2">
                          {language === 'ar' ? category.name_ar : category.name_en}
                        </p>
                        <div className="grid gap-2">
                          {categoryServices.map(service => (
                            <label
                              key={service.id}
                              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                                formData.service_id === service.id
                                  ? 'bg-primary/20 border-primary border'
                                  : 'bg-card border border-white/10 hover:border-white/30'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <input
                                  type="radio"
                                  name="service"
                                  value={service.id}
                                  checked={formData.service_id === service.id}
                                  onChange={() => handleServiceSelect(service.id)}
                                  className="accent-primary"
                                />
                                <div>
                                  <p className="text-white font-medium">
                                    {language === 'ar' ? service.name_ar : service.name_en}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {service.duration_minutes} {language === 'ar' ? 'دقيقة' : 'min'}
                                  </p>
                                </div>
                              </div>
                              <span className="text-primary font-bold">{service.price} د.إ</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Barber Selection (Optional) */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Scissors className="w-4 h-4 inline me-1" />
                  {language === 'ar' ? 'اختر الحلاق (اختياري)' : 'Select Barber (Optional)'}
                </label>
                <select
                  value={formData.barber_id}
                  onChange={(e) => setFormData({ ...formData, barber_id: e.target.value })}
                  className="w-full bg-secondary/50 border border-white/10 rounded-lg px-4 py-2 text-white"
                >
                  <option value="">{language === 'ar' ? 'أي حلاق متاح' : 'Any available barber'}</option>
                  {barbers.map(barber => (
                    <option key={barber.id} value={barber.id}>{barber.name}</option>
                  ))}
                </select>
              </div>

              {/* Date & Time */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <Calendar className="w-4 h-4 inline me-1" />
                    {language === 'ar' ? 'التاريخ' : 'Date'} *
                  </label>
                  <Input
                    type="date"
                    value={formData.booking_date}
                    onChange={(e) => setFormData({ ...formData, booking_date: e.target.value })}
                    min={getMinDate()}
                    max={getMaxDate()}
                    className="bg-secondary/50 border-white/10"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <Clock className="w-4 h-4 inline me-1" />
                    {language === 'ar' ? 'الوقت' : 'Time'} *
                  </label>
                  <Input
                    type="time"
                    value={formData.booking_time}
                    onChange={(e) => setFormData({ ...formData, booking_time: e.target.value })}
                    className="bg-secondary/50 border-white/10"
                    required
                  />
                </div>
              </div>

              {/* Deposit Options */}
              <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                <label className="block text-sm font-medium mb-3">
                  <CreditCard className="w-4 h-4 inline me-1" />
                  {language === 'ar' ? 'اختر قيمة العربون:' : 'Select Deposit Amount:'}
                </label>
                
                {selectedService ? (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                      {getDepositOptions().map(option => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            setDepositOption(option.id);
                            if (option.id !== 'custom') setCustomDeposit('');
                          }}
                          className={`p-3 rounded-lg border text-center transition-all ${
                            depositOption === option.id
                              ? 'bg-primary text-black border-primary'
                              : 'bg-secondary/50 border-white/10 text-white hover:border-primary/50'
                          }`}
                        >
                          {option.percentage && <div className="text-xs opacity-80">{option.percentage}</div>}
                          <div className="font-bold">{option.amount !== null ? `${option.amount} د.إ` : '---'}</div>
                          <div className="text-xs">{option.label}</div>
                        </button>
                      ))}
                    </div>
                    
                    {/* Custom Amount Input */}
                    {depositOption === 'custom' && (
                      <div className="mb-4 p-3 bg-secondary/30 rounded-lg">
                        <label className="block text-sm mb-2">
                          {language === 'ar' ? 'أدخل المبلغ المخصص:' : 'Enter custom amount:'}
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={customDeposit}
                            onChange={(e) => setCustomDeposit(e.target.value)}
                            min={MIN_DEPOSIT}
                            max={selectedService.price}
                            placeholder={`${MIN_DEPOSIT} - ${selectedService.price}`}
                            className="flex-1 px-3 py-2 bg-secondary border border-white/10 rounded-lg text-white text-center"
                          />
                          <span className="text-primary font-bold">د.إ</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {language === 'ar' 
                            ? `الحد الأدنى ${MIN_DEPOSIT} د.إ - الحد الأقصى ${selectedService.price} د.إ`
                            : `Min ${MIN_DEPOSIT} AED - Max ${selectedService.price} AED`}
                        </p>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between pt-3 border-t border-white/10">
                      <span className="text-white font-medium">
                        {language === 'ar' ? 'العربون المطلوب:' : 'Required Deposit:'}
                      </span>
                      <span className="text-2xl font-bold text-primary">
                        {calculateDepositAmount()} د.إ
                      </span>
                    </div>
                    
                    <div className="mt-2 text-xs text-muted-foreground">
                      <p>• {language === 'ar' ? `سعر الخدمة: ${selectedService.price} د.إ` : `Service price: ${selectedService.price} AED`}</p>
                      <p>• {language === 'ar' ? 'العربون يُخصم من إجمالي الخدمة' : 'Deposit deducted from total'}</p>
                      <p>• {language === 'ar' ? 'غير قابل للاسترداد' : 'Non-refundable'}</p>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <span className="text-white font-medium">
                        {language === 'ar' ? 'الحد الأدنى للعربون:' : 'Minimum Deposit:'}
                      </span>
                      <span className="text-2xl font-bold text-primary">
                        {MIN_DEPOSIT} د.إ
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {language === 'ar' 
                        ? 'اختر خدمة لرؤية خيارات العربون'
                        : 'Select a service to see deposit options'}
                    </p>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-black font-bold py-6"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 me-2" />
                    {language === 'ar' ? 'ادفع العربون واحجز' : 'Pay Deposit & Book'}
                    <Arrow className="w-5 h-5 ms-2" />
                  </>
                )}
              </Button>
            </form>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && bookingResult && (
          <div className="bg-card border border-white/5 rounded-xl p-6 shadow-2xl text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2">
              {language === 'ar' ? 'تم الحجز بنجاح!' : 'Booking Confirmed!'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {language === 'ar' 
                ? 'تم استلام دفعتك وتأكيد حجزك'
                : 'Your payment has been received and booking confirmed'}
            </p>

            {/* Reference Number */}
            <div className="bg-primary/10 border border-primary/30 rounded-xl p-6 mb-6">
              <p className="text-sm text-muted-foreground mb-2">
                {language === 'ar' ? 'رقم المرجع:' : 'Reference Number:'}
              </p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-3xl font-bold text-primary tracking-wider">
                  {bookingResult.reference_number}
                </span>
                <Button
                  onClick={copyReferenceNumber}
                  variant="ghost"
                  size="sm"
                  className="text-primary hover:bg-primary/10"
                >
                  <Copy className="w-5 h-5" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {language === 'ar' 
                  ? 'احتفظ بهذا الرقم للاستعلام عن حجزك'
                  : 'Keep this number to inquire about your booking'}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => window.open(`https://wa.me/971569810665?text=${encodeURIComponent(
                  language === 'ar' 
                    ? `مرحباً، رقم حجزي: ${bookingResult.reference_number}`
                    : `Hello, my booking reference: ${bookingResult.reference_number}`
                )}`, '_blank')}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <MessageCircle className="w-5 h-5 me-2" />
                {language === 'ar' ? 'تواصل عبر الواتساب' : 'Contact via WhatsApp'}
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="flex-1"
              >
                {language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PublicBooking;
